import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Project,
  NewProject,
  PagedResponse,
  EditProject,
  SortBy,
  SortDirection,
  Standard,
  Calculation,
  TemperatureDetails,
  CalculationDetails,
  SpansAndLoads,
  ProductDetails,
  Fasteners,
  ZoneDetails,
  CalculationReportSelection,
  ManufacturerFile,
  UserStandard,
} from '../../types/domainTypes';
import { getAppConfig } from '../../config/config';
import { prepareAuthHeaders } from '../../config/authConfig';
import { Division } from '../../types/domainTypes';

export const projectApi = createApi({
  reducerPath: 'projectApi',
  baseQuery: fetchBaseQuery({ prepareHeaders: prepareAuthHeaders }),
  tagTypes: ['Project'],
  endpoints: (builder) => ({
    getProjects: builder.query<
      PagedResponse,
      {
        pageSize?: number;
        pageNumber?: number;
        sortBy: SortBy;
        sortDirection: SortDirection;
        division: string;
        search: string;
      }
    >({
      query: (args) => {
        const url = `${getAppConfig().apiUrl}/Project`;
        return { url, method: 'GET', params: { ...args } };
      },
    }),
    getProject: builder.query<Project, string>({
      query: (projectId) => ({
        url: `${getAppConfig().apiUrl}/Project/${projectId}`,
        method: 'GET',
      }),
    }),
    saveProject: builder.mutation<Project, NewProject | EditProject>({
      query: (project) => ({
        url: `${getAppConfig().apiUrl}/Project/`,
        method: 'POST',
        body: project,
      }),
      invalidatesTags: ['Project'],
    }),
    getCalculation: builder.query<
      Calculation,
      {
        projectId: string;
        calculationId: string;
      }
    >({
      query: (args) => {
        const { projectId, calculationId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation/${calculationId}`,
          method: 'GET',
        };
      },
    }),
    getCalculations: builder.query<CalculationReportSelection[], string>({
      query: (projectId) => ({
        url: `${getAppConfig().apiUrl}/Project/${projectId}/calculations`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0.0001,
    }),
    saveStandard: builder.mutation<null, { standard: Standard; projectId: string }>({
      query: (args) => {
        const { standard, projectId } = args;
        return {
          url: `${getAppConfig().apiUrl}/Project/${projectId}/standard`,
          method: 'POST',
          body: standard,
        };
      },
    }),
    saveCalculation: builder.mutation<
      Calculation,
      {
        calculation: Calculation;
        projectId: string;
      }
    >({
      query: (args) => {
        const { calculation, projectId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation`,
          method: 'POST',
          body: calculation,
        };
      },
    }),
    saveCalculationDetails: builder.mutation<
      Calculation,
      { calculationDetails: CalculationDetails; projectId: string; calculationId: string }
    >({
      query: (args) => {
        const { calculationDetails, projectId, calculationId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation/${calculationId}`,
          method: 'PUT',
          body: calculationDetails,
        };
      },
    }),
    saveTemperatureDetails: builder.mutation<
      null,
      {
        temperatureDetails: TemperatureDetails;
        projectId: string;
        calculationId: string;
      }
    >({
      query: (args) => {
        const { temperatureDetails, projectId, calculationId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation/${calculationId}/temperature`,
          method: 'POST',
          body: temperatureDetails,
        };
      },
    }),
    saveCalculationStandard: builder.mutation<
      Standard,
      {
        standard: Standard;
        calculationId: string;
        projectId: string;
      }
    >({
      query: (args) => {
        const { standard, calculationId, projectId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation/${calculationId}/standard`,
          method: 'POST',
          body: standard,
        };
      },
    }),
    saveCalculationSpansAndLoads: builder.mutation<
      SpansAndLoads,
      { spansAndLoads: SpansAndLoads; calculationId: string; projectId: string }
    >({
      query: (args) => {
        const { spansAndLoads, calculationId, projectId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation/${calculationId}/spansandloads`,
          method: 'POST',
          body: spansAndLoads,
        };
      },
    }),
    saveProductDetails: builder.mutation<
      ProductDetails,
      {
        productDetails: ProductDetails;
        projectId: string;
        calculationId: string;
      }
    >({
      query: (args) => {
        const { productDetails, projectId, calculationId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation/${calculationId}/product`,
          method: 'POST',
          body: productDetails,
        };
      },
    }),
    saveFastenersDetails: builder.mutation<
      Fasteners,
      {
        fastenersDetails: Fasteners;
        projectId: string;
        calculationId: string;
      }
    >({
      query: (args) => {
        const { fastenersDetails, projectId, calculationId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation/${calculationId}/fasteners`,
          method: 'POST',
          body: fastenersDetails,
        };
      },
    }),
    saveZoneDetails: builder.mutation<
      ZoneDetails,
      {
        zoneDetails: ZoneDetails;
        projectId: string;
        calculationId: string;
      }
    >({
      query: (args) => {
        const { zoneDetails, projectId, calculationId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation/${calculationId}/zones`,
          method: 'POST',
          body: zoneDetails,
        };
      },
    }),
    deleteCalculation: builder.mutation<
      null,
      {
        projectId: string;
        calculationId: string;
      }
    >({
      query: (args) => {
        const { projectId, calculationId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation/${calculationId}`,
          method: 'DELETE',
        };
      },
    }),
    deleteFasteners: builder.mutation<
      null,
      {
        projectId: string;
        calculationId: string;
      }
    >({
      query: (args) => {
        const { projectId, calculationId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation/${calculationId}/fasteners`,
          method: 'DELETE',
        };
      },
    }),
    deleteTemperature: builder.mutation<
      null,
      {
        projectId: string;
        calculationId: string;
      }
    >({
      query: (args) => {
        const { projectId, calculationId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation/${calculationId}/temperature`,
          method: 'DELETE',
        };
      },
    }),
    deleteZone: builder.mutation<
      null,
      {
        projectId: string;
        calculationId: string;
        zoneId: string;
      }
    >({
      query: (args) => {
        const { projectId, calculationId, zoneId } = args;
        return {
          url: `${getAppConfig().apiUrl}/project/${projectId}/calculation/${calculationId}/zones/${zoneId}`,
          method: 'DELETE',
        };
      },
    }),
    getManufacturerFile: builder.query<
      ManufacturerFile[],
      {
        division: Division;
        applicationType: 'Roof' | 'Wall';
        panelType: string | undefined;
        externalFaceThickness: string | undefined;
      }
    >({
      query: (args) => {
        const { division, applicationType, panelType, externalFaceThickness } = args;
        return {
          url: `${
            getAppConfig().apiUrl
          }/admin/getManufacturerData?division=${division}&applicationType=${applicationType}&panelType=${panelType}&externalFaceThickness=${externalFaceThickness}`,
          method: 'GET',
        };
      },
    }),
    getManufacturerNames: builder.query<
      string[],
      {
        division: Division;
        applicationType: 'Roof' | 'Wall';
        panelType: string | undefined;
        externalFaceThickness: string | undefined;
      }
    >({
      query: (args) => {
        const { division, applicationType, panelType, externalFaceThickness } = args;

        return {
          url: `${
            getAppConfig().apiUrl
          }/admin/getManufacturerNames?division=${division}&applicationType=${applicationType}&panelType=${panelType}&externalFaceThickness=${externalFaceThickness}`,
          method: 'GET',
        };
      },
    }),
    saveUserStandardTemplate: builder.mutation<null, { userStandard: UserStandard }>({
      query: (args) => {
        const { userStandard } = args;

        return {
          url: `${getAppConfig().apiUrl}/admin/saveUserStandard`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: userStandard,
        };
      },
    }),
    getUserStandardTemplate: builder.query<UserStandard, string>({
      query: (user: string) => ({
        url: `${getAppConfig().apiUrl}/admin/getUserStandard?user=${user}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useGetCalculationQuery,
  useGetCalculationsQuery,
  useSaveProjectMutation,
  useSaveStandardMutation,
  useSaveCalculationMutation,
  useSaveCalculationDetailsMutation,
  useSaveTemperatureDetailsMutation,
  useSaveCalculationStandardMutation,
  useSaveCalculationSpansAndLoadsMutation,
  useSaveProductDetailsMutation,
  useSaveFastenersDetailsMutation,
  useSaveZoneDetailsMutation,
  useDeleteCalculationMutation,
  useDeleteFastenersMutation,
  useDeleteTemperatureMutation,
  useDeleteZoneMutation,
  useGetManufacturerFileQuery,
  useGetManufacturerNamesQuery,
  useSaveUserStandardTemplateMutation,
  useGetUserStandardTemplateQuery,
  util,
} = projectApi;
